import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import Button from '../components/UI/Button'
import AddSSOModal from '../components/Modals/AddSSOModal'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import api from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { Plus, Trash2, Edit, Shield, Building2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function Integration() {
  const { hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState('sso')
  const [isSSOModalOpen, setIsSSOModalOpen] = useState(false)
  const [selectedConfig, setSelectedConfig] = useState(null)
  const queryClient = useQueryClient()

  // Fetch product integrations (SSO)
  const { data: integrationData, isLoading, error } = useQuery(
    ['product-integrations', 'sso'],
    async () => {
      const response = await api.get('/product-integrations?integration_type=sso')
      return response.data
    }
  )

  const ssoConfigurations = integrationData?.data?.integrations || []

  // Group configurations by organization_name to allow multiple email domains per organization
  const groupedConfigurations = useMemo(() => {
    const groups = {}
    
    ssoConfigurations.forEach((config) => {
      // Group by organization_name (allows multiple email domains per organization)
      // Use organization_name as the primary key for grouping
      const orgKey = config.organization_name || 'Unknown Organization'
      
      if (!groups[orgKey]) {
        groups[orgKey] = {
          organizationName: orgKey,
          emailDomains: new Set(), // Track all email domains for this organization
          providers: []
        }
      }
      
      // Add email domain to the set if it exists
      if (config.email_domain) {
        groups[orgKey].emailDomains.add(config.email_domain)
      }
      
      groups[orgKey].providers.push(config)
    })
    
    // Convert Set to Array for each group
    Object.keys(groups).forEach(orgKey => {
      groups[orgKey].emailDomains = Array.from(groups[orgKey].emailDomains)
    })
    
    // Sort providers within each organization by created date
    Object.keys(groups).forEach(orgKey => {
      groups[orgKey].providers.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      )
    })
    
    return groups
  }, [ssoConfigurations])

  const handleSSOAdded = () => {
    // Invalidate both the specific query and the general query to ensure refresh
    queryClient.invalidateQueries(['product-integrations', 'sso'])
    queryClient.invalidateQueries(['product-integrations'])
    setIsSSOModalOpen(false)
    setSelectedConfig(null)
  }

  const handleCloseModal = () => {
    setIsSSOModalOpen(false)
    setSelectedConfig(null)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-secondary-900">Integration</h1>
        <p className="text-secondary-600 mt-1">Manage integrations and API connections</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('sso')}
            className={`
              py-4 px-1 border-b-2 font-medium text-sm
              ${
                activeTab === 'sso'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
              }
            `}
          >
            SSO
          </button>
        </nav>
      </div>

      {/* SSO Tab Content */}
      {activeTab === 'sso' && (
        <div className="space-y-6">
          {/* Actions */}
          {hasPermission('integration.create') && (
            <div className="flex items-center justify-end">
              <Button
                onClick={() => {
                  setSelectedConfig(null)
                  setIsSSOModalOpen(true)
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add SSO
              </Button>
            </div>
          )}

          {/* SSO Content */}
          <Card>
            <CardContent className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner />
                </div>
              ) : error ? (
                <div className="py-12 text-center">
                  <p className="text-red-600">Error loading SSO configurations</p>
                </div>
              ) : ssoConfigurations.length === 0 ? (
                <div className="py-12 text-center">
                  <Shield className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
                  <p className="text-secondary-600">
                    No SSO configurations found. Click "Add SSO" to create a new configuration.
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {Object.keys(groupedConfigurations).map((orgKey) => {
                    const org = groupedConfigurations[orgKey]
                    return (
                      <div key={orgKey} className="border border-secondary-200 rounded-lg overflow-hidden">
                        {/* Organization Header */}
                        <div className="bg-secondary-50 px-6 py-4 border-b border-secondary-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Building2 className="h-5 w-5 text-secondary-600" />
                              <h3 className="font-semibold text-secondary-900">{org.organizationName}</h3>
                            </div>
                            <div className="text-sm text-secondary-600">
                              {org.providers.length} {org.providers.length === 1 ? 'Provider' : 'Providers'}
                            </div>
                          </div>
                        </div>

                        {/* Providers Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-secondary-200 bg-white">
                                <th className="text-left py-3 px-6 font-semibold text-secondary-700">Provider</th>
                                <th className="text-left py-3 px-6 font-semibold text-secondary-700">Client ID</th>
                                <th className="text-left py-3 px-6 font-semibold text-secondary-700">Status</th>
                                <th className="text-left py-3 px-6 font-semibold text-secondary-700">Created</th>
                                <th className="text-right py-3 px-6 font-semibold text-secondary-700">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {org.providers.map((config) => (
                                <tr key={config.id} className="border-b border-secondary-100 hover:bg-secondary-50 last:border-b-0">
                                  <td className="py-3 px-6 text-secondary-900 capitalize font-medium">
                                    {config.provider}
                                  </td>
                                  <td className="py-3 px-6 text-secondary-700 font-mono text-sm">
                                    {config.client_id ? `${config.client_id.substring(0, 30)}...` : 'N/A'}
                                  </td>
                                  <td className="py-3 px-6">
                                    {hasPermission('integration.update') ? (
                                      <button
                                        onClick={async () => {
                                          try {
                                            const newStatus = !config.is_active
                                            await api.patch(`/product-integrations/${config.id}/toggle-status`, {
                                              is_active: newStatus
                                            })
                                            toast.success(`SSO provider ${newStatus ? 'enabled' : 'disabled'} successfully`)
                                            queryClient.invalidateQueries(['product-integrations', 'sso'])
                                            queryClient.invalidateQueries(['product-integrations'])
                                          } catch (error) {
                                            toast.error(error.response?.data?.message || `Failed to ${config.is_active ? 'disable' : 'enable'} SSO provider`)
                                          }
                                        }}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                                          config.is_active 
                                            ? 'bg-green-500 focus:ring-green-500' 
                                            : 'bg-gray-300 focus:ring-gray-400'
                                        }`}
                                        title={config.is_active ? 'Disable SSO Provider' : 'Enable SSO Provider'}
                                      >
                                        <span
                                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            config.is_active ? 'translate-x-6' : 'translate-x-1'
                                          }`}
                                        />
                                      </button>
                                    ) : (
                                      <span
                                        className={`inline-flex h-6 w-11 items-center rounded-full ${
                                          config.is_active 
                                            ? 'bg-green-500' 
                                            : 'bg-gray-300'
                                        }`}
                                        title={config.is_active ? 'Active' : 'Inactive'}
                                      >
                                        <span
                                          className={`inline-block h-4 w-4 transform rounded-full bg-white ${
                                            config.is_active ? 'translate-x-6' : 'translate-x-1'
                                          }`}
                                        />
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-3 px-6 text-secondary-600 text-sm">
                                    {new Date(config.created_at).toLocaleDateString()}
                                  </td>
                                  <td className="py-3 px-6">
                                    <div className="flex items-center justify-end space-x-2">
                                      {hasPermission('integration.update') && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setSelectedConfig(config)
                                            setIsSSOModalOpen(true)
                                          }}
                                          title="Edit SSO Configuration"
                                        >
                                          <Edit className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {hasPermission('integration.delete') && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={async () => {
                                            if (window.confirm('Are you sure you want to delete this SSO configuration?')) {
                                              try {
                                                await api.delete(`/product-integrations/${config.id}`)
                                                toast.success('SSO configuration deleted successfully')
                                                queryClient.invalidateQueries(['product-integrations', 'sso'])
                                                queryClient.invalidateQueries(['product-integrations'])
                                              } catch (error) {
                                                toast.error(error.response?.data?.message || 'Failed to delete SSO configuration')
                                              }
                                            }
                                          }}
                                          title="Delete SSO Configuration"
                                        >
                                          <Trash2 className="h-4 w-4 text-red-600" />
                                        </Button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add SSO Modal */}
      <AddSSOModal
        isOpen={isSSOModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSSOAdded}
        initialData={selectedConfig}
      />
    </div>
  )
}

